import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import comptabiliteService, { Mappage as MappageType, CompteComptable } from '../../api/comptabilite';

const CLE_LABELS: Record<string, string> = {
  CLIENT: 'Compte clients',
  FOURNISSEUR: 'Compte fournisseurs',
  VENTE_BIENS: 'Ventes de biens / marchandises',
  VENTE_SERVICES: 'Ventes de services',
  ACHAT_BIENS: 'Achats de biens / marchandises',
  ACHAT_SERVICES: 'Achats de services',
  TVA_COLLECTEE: 'TVA collectée (ventes)',
  TVA_DEDUCTIBLE: 'TVA déductible (achats)',
  FODEC_COLLECTEE: 'FODEC collecté',
  FODEC_DEDUCTIBLE: 'FODEC déductible',
  TIMBRE_FISCAL: 'Droit de timbre',
  RETENUE_SOURCE_SUBIE: 'Retenue à la source subie (ventes)',
  RETENUE_SOURCE_OPEREE: 'Retenue à la source opérée (achats)',
  BANQUE: 'Banque',
  CAISSE: 'Caisse',
  ECART_ARRONDI: "Écart d'arrondi",
};

const Mappage: React.FC = () => {
  const [mappages, setMappages] = useState<MappageType[]>([]);
  const [comptes, setComptes] = useState<CompteComptable[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        comptabiliteService.listMappages(),
        comptabiliteService.listComptes(),
      ]);
      setMappages(m);
      setComptes(c);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const change = async (cle: string, compteNumero: string) => {
    setSaving(cle);
    try {
      const updated = await comptabiliteService.updateMappage(cle, compteNumero);
      setMappages((prev) => prev.map((m) => (m.cle === cle ? updated : m)));
      toast.success('Mappage mis à jour');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paramètres comptables</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Associez chaque opération à un compte du plan. C'est ici que le module s'adapte aux
          évolutions de la loi (changement de taux ou de compte de TVA, nouvelle taxe…) :
          modifiez le compte, sans aucune intervention technique. La génération des écritures
          utilise ces correspondances.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Opération</th>
              <th className="text-left px-4 py-2 font-medium">Compte associé</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={2} className="px-4 py-6 text-center text-gray-400">Chargement…</td></tr>
            ) : mappages.map((m) => (
              <tr key={m.cle} className="border-t border-gray-100">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{CLE_LABELS[m.cle] || m.cle}</div>
                  {m.description && <div className="text-xs text-gray-400">{m.description}</div>}
                </td>
                <td className="px-4 py-3">
                  <select
                    value={m.compteNumero}
                    disabled={saving === m.cle}
                    onChange={(e) => change(m.cle, e.target.value)}
                    className="w-full max-w-md rounded-lg border border-gray-200 px-3 py-2"
                  >
                    {/* Ensure current value is present even if account list is filtered */}
                    {!comptes.some((c) => c.numero === m.compteNumero) && (
                      <option value={m.compteNumero}>{m.compteNumero} (compte introuvable)</option>
                    )}
                    {comptes.map((c) => (
                      <option key={c.id} value={c.numero}>{c.numero} — {c.libelle}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Mappage;
